import { useModal } from 'mui-modal-provider';

import {
  DialogContent,
  DialogActions,
  Button,
  Dialog,
  HexIcon,
  Icons,
  notify,
  ShowModalOutput,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { WorkloadScalingPolicy } from '@cast/types';

import { PreparedWorkload } from 'features/workload-optimization';
import { useUpdateWorkloadMutationV2 } from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';

import { PolicyIndicator } from './PolicyIndicator';

type AssignScalingPolicyProps = {
  workload: PreparedWorkload;
  newScalingPolicy: WorkloadScalingPolicy;
};

type AssignScalingPolicyDialogProps = AssignScalingPolicyProps & {
  destroy: () => void;
};

type ScalingPolicyCardProps = {
  scalingPolicy: WorkloadScalingPolicy;
};

const ScalingPolicyCard = ({ scalingPolicy }: ScalingPolicyCardProps) => {
  return (
    <Stack
      direction="row"
      gap={6}
      width={232}
      bgcolor={tokens.theme.bg.structural.surface}
      borderRadius="4px"
      border={tokens.theme.border.structural.neutralSubtle}
      px={8}
      py={4}
      alignItems="center"
    >
      <PolicyIndicator
        managementOption={scalingPolicy.recommendationPolicies.managementOption}
      />
      <Typography variant="P12R">{scalingPolicy?.name}</Typography>
    </Stack>
  );
};

const AssignScalingPolicyDialog = ({
  newScalingPolicy,
  workload,
  destroy,
}: AssignScalingPolicyDialogProps) => {
  const { clusterId, id, name, verticalScalingPolicy } = workload;

  const { mutate } = useUpdateWorkloadMutationV2();

  const onConfirm = () => {
    mutate(
      {
        workloadId: id,
        clusterId: clusterId,
        workload: {
          scalingPolicyId: newScalingPolicy.id,
        },
      },
      {
        onSuccess: () => {
          destroy();
          notify.success(`Scaling policy changed`, {
            key: 'succeeded-to-change-scaling-policy',
          });
        },
        onError: () =>
          notify.error('Failed to change scaling policy', {
            key: 'failed-to-change-scaling-policy',
          }),
      }
    );
  };

  return (
    <Dialog
      maxWidth="sm"
      open
      onClose={destroy}
      disableCloseOnBackdropClick
      testId="assign-scaling-policy-dialog"
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <Stack
        direction="row"
        justifyContent="center"
        sx={{
          position: 'absolute',
          top: -1 * (96 / 2),
          left: 0,
          width: '100%',
        }}
      >
        <HexIcon variant="check" icon={<Icons.Question />} size={96} />
      </Stack>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 46,
          paddingBottom: 16,
        }}
      >
        <Typography variant="A1" color="textSecondary">
          {name}
        </Typography>
        <Typography variant="h5">Change scaling policy?</Typography>
        <Typography
          variant="P12R"
          color="textSecondary"
          textAlign="center"
          pt={8}
        >
          This action will update the workload’s recommendations based on the
          new scaling policy settings.
        </Typography>
      </DialogContent>
      <Stack
        width="100%"
        bgcolor={tokens.theme.bg.status.infoSubtle}
        px={32}
        py={20}
        direction="row"
        gap={8}
        alignItems="center"
      >
        {verticalScalingPolicy && (
          <>
            <ScalingPolicyCard scalingPolicy={verticalScalingPolicy} />

            <Icons.ArrowRight
              color={tokens.theme.fg.structural.secondary}
              size={16}
            />
          </>
        )}
        <ScalingPolicyCard scalingPolicy={newScalingPolicy} />
      </Stack>
      <DialogActions sx={{ justifyContent: 'center', pt: 24 }}>
        <Button variant="secondary" onClick={destroy} testId="cancel-button">
          Cancel
        </Button>
        <Button onClick={onConfirm} testId="assign-scaling-policy-button">
          Change
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useAssignScalingPolicyDialog = () => {
  const { showModal } = useModal();

  let modalInstance: ShowModalOutput;

  const openModal = ({
    workload,
    newScalingPolicy,
  }: AssignScalingPolicyProps) => {
    modalInstance = showModal(AssignScalingPolicyDialog, {
      workload,
      newScalingPolicy,
      destroy: () => modalInstance.destroy(),
    });
  };

  return { openAssignPolicyModal: openModal };
};
